import { CategoryTaskPage } from './components/CategoryTaskPage'
import { BuilderSessionProvider } from '../TaskCreation/contexts/BuilderSessionContext'
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { UNSAFE_NavigationContext, useLocation, useNavigate } from 'react-router-dom'
import { FormStepProvider, TaskFormData, useFormStepContext } from 'components/TasksV2/TaskCreation/FormStepContext'
import { clearTaskCategoriesFromStorage } from 'components/TasksV2/TaskCreation/Steps/SubtaskCreationStep/Widgets/CategorySelector'
import { clearDraft, IDraftData, loadDraft, saveDraft } from './draftStore'
import { useCreateTaskV2 } from './hooks/useCreateTaskV2'
import ConfirmationPopup from 'components/CommonComponents/ConfirmationPopup'
import useLocaleText from 'components/useLocaleText'
import { AIGenerationProvider } from 'components/TasksV2/AITaskCreation/AIGenerationProvider'
import { PromptGeneratorProvider } from 'components/TasksV2/AITaskCreation/PromptGeneratorContext'
import { useAiSubtaskDraft } from 'components/TasksV2/AITaskCreation/hooks/useAiSubtaskDraft'
import useFeatureFlag from 'hooks/useFeatureFlag'
import { FEATURE_FLAGS } from 'constants/featureFlags'

// ─── Inner (must be child of FormStepProvider) ────────────────────────────────

interface IInnerProps {
  onDismiss?: () => void
  initialDraft: IDraftData | null
  /** Shared ref — inner component sets this so the wrapper's onClose can unblock before navigating */
  unblockRef: React.MutableRefObject<() => void>
}

const CreateTaskV2Inner: React.FC<IInnerProps> = ({ initialDraft, unblockRef, onDismiss }) => {
  const navigate = useNavigate()
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)

  const card_confirm_text = useLocaleText('card_confirm_text')
  const card_confirm_desc = useLocaleText('card_confirm_desc')
  const yes = useLocaleText('yes')
  const no = useLocaleText('no')

  // ── Block all React Router navigation while on this page ─────────────────────
  const { navigator } = useContext(UNSAFE_NavigationContext)

  const pendingNavRef = useRef<{ retry: () => void } | null>(null)

  const { pageStep, savedContentData, goToSettings, handleBackOnSettings } = useCreateTaskV2(initialDraft)
  const { isSubmitting, isMediaUploading, isSubtaskAiGenerating } = useFormStepContext()

  const isNavigationBusy = isSubmitting || isMediaUploading || isSubtaskAiGenerating

  // ── Step-1 draft persistence ─────────────────────────────────────────────────
  // getFormData() reads from a ref — no state subscription, no re-renders here.
  const { getFormData } = useFormStepContext()

  const savedContentDataRef = useRef(savedContentData)

  const saveSettingsDraftRef = useRef<() => void>(() => {})

  // Cancel/back:
  //  - step 0: open confirmation (same popup as blocked navigation)
  //  - step 1 sub-steps: go back in settings (no navigation)
  const handleBack = () => {
    if (isNavigationBusy) return
    if (pageStep === 0) {
      pendingNavRef.current = null // no blocked tx — manual cancel
      setIsConfirmOpen(true)
    } else {
      handleBackOnSettings()
    }
  }

  const handleConfirmClose = () => {
    if (isNavigationBusy) return
    setIsConfirmOpen(false)
    unblockRef.current() // remove block before any navigation
    clearTaskCategoriesFromStorage()
    clearDraft()
    if (onDismiss) {
      onDismiss()
    } else if (pendingNavRef.current) {
      // A real navigation was blocked — let it proceed
      pendingNavRef.current.retry()
      pendingNavRef.current = null
    } else {
      // Cancel button — go back manually
      if (window.history.length > 1) navigate(-1)
      else navigate('/')
    }
  }

  useEffect(() => {
    if (onDismiss) return

    // react-router's block() internally calls window.addEventListener('beforeunload', handler)
    // where handler calls event.preventDefault() — which triggers Chrome's "Reload site?" dialog.
    // We intercept window.addEventListener temporarily while block() runs so that react-router's
    // beforeunload registration is silently swallowed. We also intercept removeEventListener so
    // unblock() doesn't throw when it tries to remove its (never-registered) handler.
    let interceptedHandler: EventListenerOrEventListenerObject | null = null

    const origAdd = window.addEventListener.bind(window)
    const origRemove = window.removeEventListener.bind(window)
    ;(window as any).addEventListener = (type: string, listener: any, opts?: any) => {
      if (type === 'beforeunload') {
        interceptedHandler = listener
        return // do NOT register — this kills the native dialog
      }
      origAdd(type, listener, opts)
    }

    const unblock = (navigator as any).block((tx: { retry: () => void }) => {
      pendingNavRef.current = tx
      setIsConfirmOpen(true)
    })

    // Restore addEventListener immediately after block() has run
    ;(window as any).addEventListener = origAdd

    // Patch removeEventListener so unblock() can clean up without errors
    ;(window as any).removeEventListener = (type: string, listener: any, opts?: any) => {
      if (type === 'beforeunload' && listener === interceptedHandler) {
        return // handler was never added; nothing to remove
      }
      origRemove(type, listener, opts)
    }

    unblockRef.current = unblock

    return () => {
      unblock()
      ;(window as any).removeEventListener = origRemove
    }
  }, [navigator, unblockRef, onDismiss])

  useEffect(() => {
    savedContentDataRef.current = savedContentData
  }, [savedContentData])

  useEffect(() => {
    saveSettingsDraftRef.current = () => {
      const content = savedContentDataRef.current

      if (content) {
        saveDraft({
          ...content,
          settingsData: getFormData?.() as Record<string, unknown>,
          pageStep: 1,
        })
      }
    }
  }, [getFormData])

  useEffect(() => {
    if (pageStep !== 1) return

    const save = () => saveSettingsDraftRef.current()

    const interval = setInterval(save, 3_000)

    window.addEventListener('beforeunload', save)
    return () => {
      clearInterval(interval)
      window.removeEventListener('beforeunload', save)
    }
  }, [pageStep])

  return (
    <>
      <CategoryTaskPage
        step={pageStep}
        content={savedContentData}
        onNext={goToSettings}
        onBack={handleBack}
        onRequestClose={() => setIsConfirmOpen(true)}
        onClose={handleConfirmClose}
      />
      <ConfirmationPopup
        isOpen={isConfirmOpen}
        onConfirm={handleConfirmClose}
        onCancel={() => {
          setIsConfirmOpen(false)
          pendingNavRef.current = null
        }}
        title={card_confirm_text}
        description={card_confirm_desc}
        confirmButtonText={yes}
        cancelButtonText={no}
        isLoading={!!isNavigationBusy}
      />
    </>
  )
}

/**
 * Keeps generation and prompt-helper state available to category creation for the whole page.
 * Skipped in edit mode, where existing subtasks are read-only.
 */
const AiEnhanceProviders: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isEdit } = useFormStepContext()
  const { writeDraftFromLLM } = useAiSubtaskDraft()
  const isAiVmEnabled = useFeatureFlag(FEATURE_FLAGS.AI_VM)

  // Without the flag the AI steps are unreachable, so the LLM/chat providers are not mounted at all.
  if (isEdit || !isAiVmEnabled) return <>{children}</>

  return (
    <AIGenerationProvider
      onGenerated={writeDraftFromLLM}
      promptSubmittedEvent="vm_ai_subtask_prompt_submitted"
      generatedEvent="vm_ai_subtask_generated"
    >
      <PromptGeneratorProvider>{children}</PromptGeneratorProvider>
    </AIGenerationProvider>
  )
}

// ─── Page wrapper ─────────────────────────────────────────────────────────────

interface ICreateTaskV2LocationState {
  tid?: string
  isEdit?: boolean
}

const CreateTaskV2: React.FC<{ onClose?: () => void }> = ({ onClose }) => {
  const navigate = useNavigate()
  const location = useLocation()

  const locationState = (onClose ? {} : location.state ?? {}) as ICreateTaskV2LocationState

  // Incrementing this key forces FormStepProvider + Inner to fully remount (fresh form)
  const [formKey, setFormKey] = useState(0)

  // Load draft once per mount — cleared on success so remount sees null
  const [initialDraft] = useState<IDraftData | null>(() => loadDraft())

  // Shared unblock ref — CreateTaskV2Inner sets this; handleClose reads it
  // so that successful task submission can unblock before navigating away.
  const unblockRef = useRef<() => void>(() => {})

  // Called by FormStepContext on successful task creation:
  // clear everything and reset to an empty form instead of leaving the page.
  const handleClose = useCallback(() => {
    unblockRef.current()
    clearTaskCategoriesFromStorage()
    clearDraft()
    if (onClose) {
      onClose()
      return
    }
    // Clear location state (remove tid/isEdit) and remount form as fresh creation flow
    navigate(location.pathname, { replace: true, state: null })
    setFormKey((k) => k + 1)
  }, [navigate, location.pathname, onClose])

  return (
    <FormStepProvider
      key={formKey}
      isEdit={Boolean(locationState.isEdit)}
      tid={locationState.tid}
      onClose={handleClose}
      isVmTask={true}
      isGroupedByCategory={true}
      initialData={formKey === 0 ? (initialDraft?.settingsData as Partial<TaskFormData> | undefined) : undefined}
    >
      <BuilderSessionProvider>
        <AiEnhanceProviders>
          <CreateTaskV2Inner
            initialDraft={formKey === 0 ? initialDraft : null}
            unblockRef={unblockRef}
            onDismiss={onClose}
          />
        </AiEnhanceProviders>
      </BuilderSessionProvider>
    </FormStepProvider>
  )
}

export default CreateTaskV2
